/**
 * Entró un equipo — la lógica, sin React.
 *
 * LO QUE ESTE MODULO CORRIGE DE RAIZ
 * ----------------------------------
 * Un iPhone no es un producto nuevo. El lineup ya está: la lista muestra todos
 * los modelos del 13 para arriba, cargados o no. Lo que de verdad pasa en el
 * local es que ENTRA UN EQUIPO —uno sellado que llegó del proveedor, o uno
 * usado que trajo un cliente— y eso es un movimiento de stock, no un alta de
 * catálogo.
 *
 * Que la única puerta fuera «Producto nuevo» obligaba a pensar al revés: para
 * anotar que entraron tres 17 Pro había que decidir primero si el 17 Pro
 * "existía", y si alguien contestaba que no, se creaba una ficha paralela a la
 * que ya estaba. Así aparecieron los dos «18 Pro Max 256 GB Black».
 *
 * Acá la pregunta es una sola: **qué entró**. La ficha se busca, y si no está
 * se crea; pero eso es una consecuencia, no una pregunta que se le hace a
 * nadie.
 *
 * QUE PREGUNTA SEGUN QUE SEA
 * --------------------------
 * Lo que cambia entre un sellado y un usado no es el formulario, es lo que se
 * sabe del aparato. De un sellado se sabe el modelo y listo: son todos iguales,
 * y por eso entran de a varios. Un usado es UNO: tiene IMEI, tiene una batería
 * medida, y por eso entra de a uno y con ficha propia.
 *
 * UN USADO SIEMPRE ES FICHA NUEVA
 * -------------------------------
 * No se busca ninguna: dos "15 Pro 128" con distinta batería son dos equipos
 * distintos, con distinto costo de toma y distinto precio. Un sellado sí se
 * busca, porque tres 17 Pro negros de 256 son tres unidades de la misma cosa.
 */
import { type Moneda } from "@tora/shared";

import { colorDeIphone } from "@/components/catalogo/colores";
import { normalizarImei } from "@/components/usados/logica";
import { aNumero, type NuevoProducto, type Producto } from "@/lib/tipos";

import { nombreDeEquipo } from "./equipo";
import { parsearIphone } from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// El formulario
// ─────────────────────────────────────────────────────────────────────────────

/**
 * De dónde viene el equipo.
 *
 * Un sellado y un usado del local ENTRAN: son stock, se pueden vender hoy. Un
 * equipo que todavía tiene el proveedor no entró —marcarlo como stock es
 * prometer algo que no está en el local—, pero hay que poder anotarlo igual,
 * porque es lo que se ofrece cuando alguien pregunta por un modelo que no
 * tenemos. Queda «a pedido», en cero, con el proveedor al que hay que llamarle.
 */
export type Origen = "local" | "proveedor";

export interface FormEntrada {
  /** "17 PRO MAX". Lo trae la fila del modelo desde la que se abrió. */
  modelo: string;
  memoriaGb: number | null;
  color: string;
  /** Lo decide la sección desde la que se abrió, no una pregunta. */
  esUsado: boolean;
  origen: Origen;
  /** A quién llamarle. Solo para los «a pedido». */
  proveedorId: number | null;

  /** Cuántos entraron. Un usado es siempre uno: tiene un IMEI. */
  unidades: number | null;

  // ── Lo que solo tiene un usado ────────────────────────────────────────────
  imei: string;
  /**
   * No lo tengo a mano ahora. La válvula de escape de una regla que si no la
   * tiene se esquiva con quince ceros — y un IMEI inventado es peor que
   * ninguno: ocupa el lugar del real.
   */
  imeiPendiente: boolean;
  bateriaPct: number | null;
  cajaCable: boolean;
  garantiaMeses: number | null;
  observaciones: string;

  // ── La plata ──────────────────────────────────────────────────────────────
  costo: number | null;
  moneda: Moneda;
  /** Se pregunta solo si la ficha todavía no tiene precio. */
  precio: number | null;
}

export const FORM_ENTRADA_INICIAL: FormEntrada = {
  modelo: "",
  memoriaGb: null,
  color: "",
  esUsado: false,
  origen: "local",
  proveedorId: null,
  unidades: 1,
  imei: "",
  imeiPendiente: false,
  bateriaPct: null,
  cajaCable: true,
  garantiaMeses: 6,
  observaciones: "",
  costo: null,
  moneda: "USD",
  precio: null,
};

/**
 * El equipo que describe una fila de modelo más un color.
 *
 * No hay formulario detrás: el ingreso de stock ya preguntó la cantidad y el
 * color, y lo único que falta es decir CUAL equipo es para poder buscar su
 * ficha o crearla. Armar un `FormEntrada` es lo que deja que `/inventario` use las
 * mismas dos funciones que el asistente —`fichaDeLaEntrada` y `fichaNueva`— en
 * vez de su propia versión, que es de donde salieron las fichas duplicadas.
 *
 * Los números salen del modelo: el precio y el costo de un color son los del
 * modelo, y lo único que cambia es cuál equipo es.
 */
export function entradaDeColor(modelo: Producto, color: string): FormEntrada {
  const partes = parsearIphone(modelo.nombre);
  return {
    ...FORM_ENTRADA_INICIAL,
    modelo: partes.modelo,
    memoriaGb: modelo.memoria_gb ?? (partes.capacidadGb || null),
    color,
    esUsado: false,
    costo: aNumero(modelo.costo),
    moneda: modelo.costo_moneda,
    precio: modelo.precio_manual === null ? null : aNumero(modelo.precio_manual),
  };
}

/** El equipo lo tiene el proveedor: no entró al local. */
export function esDeProveedor(form: FormEntrada): boolean {
  return form.origen === "proveedor";
}

// ─────────────────────────────────────────────────────────────────────────────
// Buscar la ficha
// ─────────────────────────────────────────────────────────────────────────────

/** Dos formas de escribir el mismo color: "DESERT" y "Desert Titanium". */
function mismoColor(a: string | null, b: string | null): boolean {
  const na = colorDeIphone(a)?.etiqueta ?? (a ?? "").trim().toUpperCase();
  const nb = colorDeIphone(b)?.etiqueta ?? (b ?? "").trim().toUpperCase();
  return na !== "" && na === nb;
}

/**
 * La ficha contra la que entra este stock, si ya existe.
 *
 * Se busca por lo que IDENTIFICA al equipo —modelo, capacidad y color— y no por
 * el nombre. El nombre es texto armado: comparar cadenas es lo que hacía
 * `/inventario`, y por eso el día que el nombre cambió de formato la búsqueda dejó
 * de encontrar la ficha que estaba ahí y creó una segunda.
 *
 * El color se compara por el color que la TABLA reconoce: en la base hay
 * "DESERT" y "Desert Titanium", que son el mismo.
 *
 * Un usado nunca encuentra nada, y es a propósito: cada unidad es su ficha.
 */
export function fichaDeLaEntrada(
  productos: ReadonlyArray<Producto>,
  form: FormEntrada,
): Producto | null {
  if (form.esUsado) return null;
  const modelo = form.modelo.trim().toUpperCase();
  if (!modelo || form.memoriaGb === null) return null;

  return (
    productos.find((p) => {
      if (p.es_usado) return false;
      const partes = parsearIphone(p.nombre);
      if (partes.modelo.trim().toUpperCase() !== modelo) return false;
      const capacidad = p.memoria_gb ?? partes.capacidadGb;
      if (capacidad !== form.memoriaGb) return false;
      return mismoColor(p.color, form.color);
    }) ?? null
  );
}

/**
 * Las fichas de color de un modelo sellado: las que tienen las unidades.
 *
 * La fila sin color es la lista de precios y se queda en cero; lo que hay en
 * el local vive en «IPHONE 16 128 GB · Black», «· Teal»… Contar la fila del
 * modelo sin mirarlas comparaba contra ese cero: con un Black y un Teal
 * cargados, contar 3 escribia +3 y el stock quedaba en 5.
 *
 * Mismo criterio que `fichaDeLaEntrada`: modelo y capacidad, nunca el nombre.
 */
export function fichasDeColorDelModelo(
  modelo: Producto,
  productos: ReadonlyArray<Producto>,
): Producto[] {
  if (modelo.es_usado || modelo.color) return [];
  const partes = parsearIphone(modelo.nombre);
  const nombreModelo = partes.modelo.trim().toUpperCase();
  const memoria = modelo.memoria_gb ?? (partes.capacidadGb || null);
  if (!nombreModelo || memoria === null) return [];
  return productos.filter((p) => {
    if (p.id === modelo.id || p.es_usado || !p.color || p.activo === false) return false;
    const otras = parsearIphone(p.nombre);
    if (otras.modelo.trim().toUpperCase() !== nombreModelo) return false;
    return (p.memoria_gb ?? otras.capacidadGb) === memoria;
  });
}

/**
 * La inversa de `fichasDeColorDelModelo`: la fila del modelo de una ficha de
 * color («IPHONE 14 128 GB · Midnight» → «IPHONE 14 128 GB»).
 *
 * Lo vendido a pedido deja el encargo en la fila del MODELO, con el color en
 * la nota (0114). Mirando solo la ficha de color, el ingreso no veía ese
 * encargo y el equipo del cliente entraba como stock libre.
 */
export function modeloDeLaFichaDeColor(
  ficha: Producto,
  productos: ReadonlyArray<Producto>,
): Producto | null {
  if (ficha.es_usado || !ficha.color) return null;
  const partes = parsearIphone(ficha.nombre);
  const nombreModelo = partes.modelo.trim().toUpperCase();
  const memoria = ficha.memoria_gb ?? (partes.capacidadGb || null);
  if (!nombreModelo || memoria === null) return null;
  return (
    productos.find((p) => {
      if (p.id === ficha.id || p.es_usado || p.color) return false;
      const otras = parsearIphone(p.nombre);
      if (otras.modelo.trim().toUpperCase() !== nombreModelo) return false;
      return (p.memoria_gb ?? otras.capacidadGb) === memoria;
    }) ?? null
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lo que sale del formulario
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La ficha nueva, con los números del modelo cuando los hay.
 *
 * El nombre lo arma `nombreDeEquipo` —el mismo armador que todo lo demás— y el
 * color va a SU COLUMNA. Antes `/inventario` creaba la ficha del color concatenando
 * `"<nombre del modelo> <COLOR>"`, que es exactamente lo que este cambio sacó:
 * un color adentro del nombre no lo lee ningún filtro, y ademas dejaba dos
 * formatos de nombre conviviendo.
 */
export function fichaNueva(form: FormEntrada, modelo: Producto | null): NuevoProducto {
  const nuevo: NuevoProducto = {
    nombre: nombreDeEquipo({
      modelo: form.modelo,
      memoriaGb: form.memoriaGb,
      esUsado: form.esUsado,
    }),
    color: form.color.trim().toUpperCase() || null,
    memoria_gb: form.memoriaGb,
    es_usado: form.esUsado,
    a_pedido: esDeProveedor(form),
    proveedor_id: esDeProveedor(form) ? form.proveedorId : (modelo?.proveedor_id ?? null),
    rubro_id: modelo?.rubro_id ?? null,
    marca: modelo?.marca ?? null,
    costo: form.costo ?? 0,
    costo_moneda: form.moneda,
    // El precio en la moneda del modelo si ya hay uno decidido; si no, en la
    // que se acaba de elegir.
    precio_moneda: modelo?.precio_moneda ?? form.moneda,
    precio_manual:
      form.precio ?? (modelo?.precio_manual === null ? null : aNumero(modelo?.precio_manual ?? 0)),
    // Nada se publica solo: publicar es una decisión aparte, y un equipo recién
    // cargado casi nunca tiene la foto.
    publicado: false,
  };

  if (modelo) {
    nuevo.markup_tipo = modelo.markup_tipo;
    nuevo.markup_valor = aNumero(modelo.markup_valor);
    nuevo.markup_moneda = modelo.markup_moneda;
    nuevo.redondeo = modelo.redondeo;
  }

  if (form.esUsado) {
    nuevo.imei = normalizarImei(form.imei) || null;
    nuevo.bateria_pct = form.bateriaPct;
    nuevo.caja_cable = form.cajaCable;
    nuevo.garantia_meses = form.garantiaMeses;
    nuevo.descripcion = form.observaciones.trim() || null;
  }

  return nuevo;
}
